import { createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { login, updateUser, refreshLoginData, updateUserConfig, removeUserConfigs, getUserGridSettings } from '../../api/account';
import { loginAsUser } from '../../api/user';
import { postSiteSwitch } from '../../api/home';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  user: {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    ssoLogin: false,
    siteId: 0,
    settings: [],
    sites: [],
    roles: [],
  },
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    loginSuccess(state, action) {
      if (action.payload.success) {
        state.isLoading = false;
        state.user = action.payload.user;
        if (action.payload.redirectUri) {
          const url = new URL(action.payload.redirectUri);
          url.searchParams.set('autoDisplayNotifications', true);
          window.location.href = url;
        } else {
          window.location.href = '/dashboard?autoDisplayNotifications=true';
        }
      } else {
        state.isLoading = false;
        state.error = 'Login Failed';
      }
    },

    refreshLoginSuccess(state, action) {
      if (action.payload.success) {
        state.isLoading = false;
        state.user = action.payload.user;
      } else {
        window.location.href = `/auth/login?returnUrl=${window.location.href}`;
        state.isLoading = false;
        state.error = 'Login Failed';
      }
    },

    updateUsersName(state, action) {
      state.isLoading = false;
      state.user.firstName = action.payload.firstName;
      state.user.lastName = action.payload.lastName;
    },   

    clearUser(state) {    
      state.user = initialState.user;
    },

    updateSite(state, action) {
      if (state.user.siteId !== action.payload.siteId) {
        state.user.siteId = action.payload.siteId;
        setTimeout(() => {
          if (action.payload.changeSiteCallback) action.payload.changeSiteCallback()
          else window.location.href = '/dashboard';
        }, 300)
      }
    },   

    updateSettings(state, action) {
      state.user.settings = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;
// ----------------------------------------------------------------------

export function persistLogin({ email, password, returnUrl }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await login({ email, password, returnUrl });
      if (response.success) {
        dispatch(slice.actions.loginSuccess(response));
        return response; // resolve the promise with the response
      }
      dispatch(slice.actions.hasError(response));
      return response; // resolve the promise with the response
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error; // resolve the promise with the error
    }
  };
}

export function refreshLogin(fullRefresh = false) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const response = await refreshLoginData(fullRefresh);
    dispatch(slice.actions.refreshLoginSuccess(response));
    return response;
  };
}

export function loginAsUserReq(userEmail) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await loginAsUser(userEmail);
      if (response.data) {
        sessionStorage.removeItem('hasSeenLoginModal');
        dispatch(slice.actions.loginSuccess(response.data));
        window.location.href = '/dashboard';
      } else enqueueSnackbar("Login failed", { variant: "error" });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function clearUser() {
  return async (dispatch) => {   
    dispatch(slice.actions.clearUser());
  };
}

export function updateUserSiteId(siteId, changeSiteCallback) {
  return async (dispatch) => {
    try {
      await postSiteSwitch(siteId);
      dispatch(slice.actions.updateSite({ siteId, changeSiteCallback }));
      return true;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
    return false;
  };
}

export function setUserSiteIdInRedux(siteId, changeSiteCallback) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.updateSite({ siteId, changeSiteCallback }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function updateUserInRedux(user) {
  return async (dispatch) => {
    dispatch(slice.actions.updateUsersName(user));
  };
}

export function updateSingleUser(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await updateUser(value);
      if (response) {
        dispatch(slice.actions.updateUsersName({ firstName: value.firstName, lastName: value.lastName }));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateUserSettings(userSettingsArray) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await updateUserConfig(userSettingsArray);
      if (response) {
        dispatch(slice.actions.updateSettings(response));
        return response; // Return the response here
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
    return null;
  };
}

export function removeUserSettings(userSettingsAreaId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await removeUserConfigs(userSettingsAreaId);
      if (response) {
        dispatch(slice.actions.updateSettings(response));
        return response;
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
    return null;
  };
}

export function fetchUserGridSettings(gridId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getUserGridSettings(gridId);
      if (response) {
        dispatch(slice.actions.updateGridSettings(response));
        return response;
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
    return null;
  };
}

